import { logger } from '@/utils/logger';
import React, { createContext, useContext, useState, ReactNode, useEffect, useCallback } from 'react';
import { useToast } from '@/hooks/use-toast';
import { videosApi, Video } from '@/api/upload/videos';

interface VideosContextType {
    // Videos list
    videos: Video[];
    setVideos: (videos: Video[]) => void;
    isLoading: boolean;

    // Upload
    files: File[];
    setFiles: (files: File[]) => void;
    uploadProgress: Record<string, number>;
    isUploading: boolean;

    // File input ref
    fileInputRef: React.RefObject<HTMLInputElement>;

    // Drag and drop
    isDragging: boolean;
    setIsDragging: (dragging: boolean) => void;

    // Actions
    fetchVideos: () => Promise<void>;
    uploadFiles: () => Promise<Video | null>;
    deleteVideo: (videoId: string, event?: React.MouseEvent,
        selectedVideo?: Video | null,
        onSuccess?: () => void) => Promise<void>;
    handleDragOver: (e: React.DragEvent) => void;
    handleDragLeave: (e: React.DragEvent) => void;
    handleDrop: (e: React.DragEvent) => void;
    handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => void;
    validateAndAddFiles: (newFiles: FileList | File[]) => void;
    removeFile: (index: number) => void;
}

const VideosContext = createContext<VideosContextType | undefined>(undefined);

export { VideosContext };
export type { VideosContextType };

interface VideosProviderProps {
    children: ReactNode;
}

export const VideosProvider: React.FC<VideosProviderProps> = ({ children }) => {
    const { toast } = useToast();

    // Videos list state
    const [videos, setVideos] = useState<Video[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Upload state
    const [files, setFiles] = useState<File[]>([]);
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
    const [isUploading, setIsUploading] = useState<boolean>(false);

    // File input ref
    const fileInputRef = React.useRef<HTMLInputElement>(null);

    // Drag and drop state
    const [isDragging, setIsDragging] = useState<boolean>(false);

    // Fetch videos
    const fetchVideos = useCallback(async () => {
        setIsLoading(true);
        try {
            const lastFetchTime = localStorage.getItem('lastVideosFetchTime');
            const now = Date.now();
            if (lastFetchTime && now - parseInt(lastFetchTime) < 3000) {
                setIsLoading(false);
                return;
            }

            const response = await videosApi.getVideos();
            if (response && Array.isArray(response)) {
                setVideos(response);
                localStorage.setItem('lastVideosFetchTime', now.toString());
            }
        } catch (error) {
            logger.error('Error fetching videos:', error);
            toast({
                variant: "destructive",
                title: "Failed to load videos",
                description: "There was an error loading your videos",
            });
        } finally {
            setIsLoading(false);
        }
    }, [toast]);

    // Upload video files
    const uploadFiles = useCallback(async (): Promise<Video | null> => {
        if (files.length === 0) return null;

        setIsUploading(true);
        const initialProgress: Record<string, number> = {};
        files.forEach(file => {
            initialProgress[file.name] = 0;
        });
        setUploadProgress(initialProgress);
        try {
            for (const file of files) {
                try {
                    const uploadedVideo = await videosApi.uploadVideo(file, (progress) => {
                        setUploadProgress(prev => ({
                            ...prev,
                            [file.name]: progress
                        }));
                    });

                    if (uploadedVideo) {
                        setFiles([]);
                        toast({
                            title: "Upload complete",
                            description: "Now you can define a region of interest for analysis",
                        });

                        await fetchVideos();

                        return uploadedVideo;
                    }
                } catch (fileError: unknown) {
                    setFiles(prevFiles => prevFiles.filter(f => f.name !== file.name));
                    const err = fileError as { response?: { status?: number; data?: { message?: string } }; message?: string };
                    if (err.response?.status === 413) {
                        const errorMessage = err.response?.data?.message ||
                            `File "${file.name}" is too large (max 750MB allowed)`;
                        toast({
                            variant: "destructive",
                            title: "File size exceeded",
                            description: errorMessage,
                        });
                    } else {
                        toast({
                            variant: "destructive",
                            title: "Upload failed",
                            description: `Failed to upload "${file.name}": ${err.message || 'Unknown error'}`,
                        });
                    }

                    if (files.length > 1) {
                        continue;
                    } else {
                        return null;
                    }
                }
            }
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Upload failed",
                description: "There was an unexpected error during upload",
            });
        } finally {
            setIsUploading(false);
        }

        return null;
    }, [files, toast, fetchVideos]);

    // Delete video
    const deleteVideo = useCallback(async (
        videoId: string,
        event?: React.MouseEvent,
        selectedVideo?: Video | null,
        onSuccess?: () => void
    ) => {
        if (event) {
            event.stopPropagation();
        }

        if (!window.confirm('Are you sure you want to delete this video?')) {
            return;
        }

        try {
            await videosApi.deleteVideo(videoId);
            if (selectedVideo && String(selectedVideo.uid) === String(videoId) && onSuccess) {
                onSuccess();
            }
            setVideos(prevVideos => prevVideos.filter(v => String(v.uid) !== String(videoId)));

            toast({
                title: "Video deleted",
                description: "The video has been successfully deleted",
            });
        } catch (error) {
            toast({
                variant: "destructive",
                title: "Delete failed",
                description: "There was an error deleting the video",
            });
        }
    }, [toast]);
    const validateAndAddFiles = useCallback((newFiles: FileList | File[]) => {
        const validFiles: File[] = [];
        const allowedTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/avi'];

        for (let i = 0; i < newFiles.length; i++) {
            const file = newFiles[i];
            if (allowedTypes.includes(file.type)) {
                validFiles.push(file);
            } else {
                toast({
                    variant: "destructive",
                    title: "Invalid file type",
                    description: `${file.name} is not a supported video format. Please use MP4, MOV, or AVI.`,
                });
            }
        }

        if (validFiles.length > 0) {
            setFiles(prevFiles => [...prevFiles, ...validFiles]);
        }
    }, [toast]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragging(false);

        if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
            validateAndAddFiles(e.dataTransfer.files);
        }
    }, [validateAndAddFiles]);

    const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files.length > 0) {
            validateAndAddFiles(e.target.files);
        }
    }, [validateAndAddFiles]);

    const removeFile = useCallback((index: number) => {
        setFiles(prevFiles => {
            const newFiles = [...prevFiles];
            newFiles.splice(index, 1);
            return newFiles;
        });
    }, []);

    return (
        <VideosContext.Provider
            value={{
                videos,
                setVideos,
                isLoading,
                files,
                setFiles,
                uploadProgress,
                isUploading,
                fileInputRef,
                isDragging,
                setIsDragging,
                fetchVideos,
                uploadFiles,
                deleteVideo,
                handleDragOver,
                handleDragLeave,
                handleDrop,
                handleFileSelect,
                validateAndAddFiles,
                removeFile,
            }}
        >
            {children}
        </VideosContext.Provider>
    );
};
